import { format, isSameYear } from 'date-fns'

import type { ClientPhone, PatientRecord } from '@/features/patients/types'

/** Values `app_patients.is_veteran` carries, lower-cased. */
const VETERAN_TRUE = new Set(['true', 'veteran', 'veterans', 'yes', 'y'])
const VETERAN_FALSE = new Set(['false', 'non-veteran', 'no', 'n'])

/**
 * Render `is_veteran`, which is text and not a boolean.
 *
 * Observed values include 'true', 'false', 'Veterans' and 'Chose not to
 * disclose'. Two failure modes to avoid: a truthiness check reports the string
 * 'false' as a veteran, and folding everything unrecognized into "No" turns a
 * declined answer into a negative one. Anything outside the known sets is
 * returned as written.
 */
export function formatVeteranStatus(value: string | null): string | null {
  const trimmed = value?.trim()
  if (!trimmed) return null
  const normalized = trimmed.toLowerCase()
  if (VETERAN_TRUE.has(normalized)) return 'Veteran'
  if (VETERAN_FALSE.has(normalized)) return 'Non-veteran'
  return trimmed
}

/** Up to two initials for the header avatar. */
export function clientInitials(patient: PatientRecord): string {
  const initials = [patient.first_name, patient.last_name]
    .map((part) => part?.trim().charAt(0).toUpperCase())
    .filter((char): char is string => !!char)
    .join('')
  return initials || '?'
}

/**
 * Whole years between a YYYY-MM-DD date of birth and today.
 *
 * Parsed as a local calendar date, matching `formatDateOfBirth` — treating the
 * string as UTC shifts the date a day west of Greenwich and can move a birthday
 * across a year boundary.
 */
export function clientAgeYears(
  dateOfBirth: string | null,
  now: Date = new Date()
): number | null {
  if (!dateOfBirth) return null
  const parts = dateOfBirth.split('-').map((part) => Number.parseInt(part, 10))
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part)))
    return null
  const [year, month, day] = parts as [number, number, number]

  let age = now.getFullYear() - year
  const monthDelta = now.getMonth() - (month - 1)
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < day)) age -= 1

  return age >= 0 && age < 150 ? age : null
}

/**
 * Every number on the record, first entry first.
 *
 * Relay flattens `Phone` to the first entry's number for the table column, so
 * `phones` may be empty while `phone` is set; that case yields one unlabelled
 * entry rather than an empty contact card.
 *
 * `phones` is read defensively rather than trusted: rows also arrive from a
 * persisted React Query cache written by an earlier build, and a missing array
 * would otherwise throw here and take the whole clients list down with it.
 */
export function clientPhones(patient: PatientRecord): ClientPhone[] {
  if (Array.isArray(patient.phones) && patient.phones.length > 0) {
    // The same number can appear twice after a re-ingest; show it once.
    const seen = new Set<string>()
    return patient.phones.filter((phone) => {
      if (seen.has(phone.phone_number)) return false
      seen.add(phone.phone_number)
      return true
    })
  }
  if (!patient.phone) return []
  return [{ phone_number: patient.phone, label: null, status: null }]
}

/**
 * True when a line is flagged as anything other than active by the source
 * system. Used to mark a number that should not be the first one dialled.
 */
export function isPhoneInactive(phone: ClientPhone): boolean {
  const status = phone.status?.trim().toLowerCase()
  return !!status && status !== 'active'
}

/** A US ZIP anywhere in the string, 5-digit or ZIP+4. */
const CONTAINS_ZIP = /\b\d{5}(-\d{4})?\b/

/**
 * The client's location as one line.
 *
 * `zip_code` is appended only when the address carries no ZIP of its own: the
 * two columns can disagree on a record, and appending unconditionally renders
 * two different ZIPs that read as a typo, so the formatted address wins. A
 * record with a ZIP but no street address still shows the ZIP.
 */
export function clientAddress(patient: PatientRecord): string | null {
  const address = patient.address?.trim()
  const zip = patient.zip_code?.trim()
  if (!address) return zip || null
  if (!zip || CONTAINS_ZIP.test(address)) return address
  return `${address} ${zip}`
}

/** "Aug 19" this year, "Aug 19, 2025" otherwise — dates in the sheet are recent. */
export function formatShortDate(date: Date, now: Date): string {
  return format(date, isSameYear(date, now) ? 'MMM d' : 'MMM d, yyyy')
}

/** "Aug 2026" — a care-team chip only needs the month of the last visit. */
export function formatMonthYear(date: Date): string {
  return format(date, 'MMM yyyy')
}

/** Words a payer or place name keeps lower-case mid-phrase. */
const MINOR_WORDS = new Set(['of', 'and', 'the', 'for', 'in', 'on', 'at'])

/**
 * Normal case for values a source system shouted: "JANE SAMPLE" →
 * "Jane Sample", "PARTNERSHIP HEALTHPLAN OF CALIFORNIA" → "Partnership
 * Healthplan of California". Mixed-case values pass through untouched, since
 * their capitalization ("McDonald", "eCW") is deliberate.
 */
export function displayCase(value: string): string {
  if (value !== value.toUpperCase() || value === value.toLowerCase()) {
    return value
  }
  return value
    .toLowerCase()
    .split(' ')
    .map((word, index) =>
      index > 0 && MINOR_WORDS.has(word)
        ? word
        : word.replace(
            /(^|[-'])([a-z])/g,
            (_match, lead: string, letter: string) =>
              `${lead}${letter.toUpperCase()}`
          )
    )
    .join(' ')
}

/** Street-type words; the city starts after the first one (and any unit). */
const STREET_SUFFIXES = new Set([
  'ST',
  'STREET',
  'AVE',
  'AVENUE',
  'RD',
  'ROAD',
  'DR',
  'DRIVE',
  'LN',
  'LANE',
  'CT',
  'COURT',
  'WAY',
  'BLVD',
  'BOULEVARD',
  'PKWY',
  'PARKWAY',
  'CIR',
  'CIRCLE',
  'PL',
  'PLACE',
  'TER',
  'TERRACE',
  'HWY',
  'HIGHWAY',
  'LOOP',
])
const UNIT_WORDS = new Set(['APT', 'UNIT', 'STE', 'SUITE', 'SPC', 'RM', '#'])
const STATE_ZIP = /^(.*?)[,\s]+([A-Za-z]{2})\.?\s+\d{5}(?:-\d{4})?\s*$/

/**
 * "San Rafael, CA" from a one-line street address, or null when the city
 * cannot be told apart from the street.
 *
 * Addresses are stored as a single string. With commas ("318 Lincoln Ave,
 * Apt 4, San Rafael, CA 94901") the city is the last comma part. Without them
 * ("12 ELM ST SAN RAFAEL CA 94901") the city is whatever follows the first
 * street-type word and any unit number. An address with neither ("GENERAL DELIVERY
 * SAN RAFAEL CA 94901") is left alone rather than guessed at.
 */
export function cityAndState(address: string | null): string | null {
  const match = STATE_ZIP.exec(address?.trim() ?? '')
  if (!match) return null
  const [, rest = '', state = ''] = match

  let city: string | null = null
  if (rest.includes(',')) {
    city = rest.split(',').at(-1)?.trim() || null
  } else {
    const words = rest.trim().split(/\s+/)
    const suffixAt = words.findIndex(
      (word, index) => index > 0 && STREET_SUFFIXES.has(word.toUpperCase())
    )
    if (suffixAt === -1) return null
    let start = suffixAt + 1
    while (UNIT_WORDS.has(words[start]?.toUpperCase() ?? '')) start += 2
    if (words[start]?.startsWith('#')) start += 1
    city = words.slice(start).join(' ') || null
  }

  return city ? `${displayCase(city)}, ${state.toUpperCase()}` : null
}
